<template>
  <div class="order-summary" :class="{ 'expanded': expanded || currentTab === 'payment' || order.upgrade, 'on-payment': currentTab === 'payment' && !order.upgrade }">
    <div class="heading" @click="expandSection($event)">
      <h2 class="h5">
        Order Summary
        <IconArrow v-if="!$store.state.auth.user" class="expand-arrow" />
      </h2>
      <div class="total-price">
        {{ order.total | formatCurrency(selectedCurrency) }}
      </div>
      <Loader v-if="loading && order.coins" type="element" class="order-summary-loader coins" />
      <div
        v-if="showCurrencyDropdown"
        class="currency"
        :class="{ loading }"
        @click="dropdownOpen = !dropdownOpen"
      >
        <div>
          {{ selectedCurrency.text }}
          <IconArrow />
        </div>
        <ul v-show="dropdownOpen">
          <template v-for="(c, id) in $store.getters['checkout/currencies']">
            <li v-if="paymentInfo.method === 'card' || (paymentInfo.method === 'pay_pal' && id !== 'INR')" :key="id" @click="$store.dispatch('checkout/setCurrency', id)">
              {{ c }} {{ id }}
            </li>
          </template>
        </ul>
      </div>
    </div>
    <div class="content">
      <div v-if="!rkey && !order.coins && !order.coupon.hidden && !['yt-campaign', 'rsc-campaign'].includes($route.query.page)" class="promo-code" :class="{ loading }">
        <Loader v-if="loading" type="element" class="order-summary-loader" />
        <div v-if="order.coupon.applied" class="promo-applied">
          <p>Promo Code applied: </p>
          <span class="promo-discount">{{ coupon }} <IconClose @click="removeCode" /></span>
        </div>
        <div v-else-if="hasPromoCode" class="promo-input-wrapper">
          <div class="promo-input">
            <div>
              <input
                v-model="coupon"
                type="text"
                placeholder="Enter Promo Code"
                :class="{ invalid: order.coupon.value && !order.coupon.applied }"
              >
              <span v-if="order.coupon.value && !order.coupon.applied">Promo code not found</span>
            </div>
            <button
              class="btn-primary btn-lg" :data-event="`${$route.query.plan}_promocode.click`" data-direct-send
              @click="$store.dispatch('checkout/fetchOrderSummary'), $store.commit('checkout/setCouponRemoved', false)"
            >
              Apply
            </button>
          </div>
          <div class="promo-text" @click="hasPromoCode = false, coupon = ''">
            I don’t have a promo code
          </div>
        </div>
        <div v-else-if="!order.changeAllocation" class="promo-text" @click="hasPromoCode = true">
          I have a promo code
        </div>
      </div>
      <div v-if="order.business || order.coins" class="subscriptions-quantity-widget">
        <div class="subscriptions-quantity">
          <template v-if="order.business">
            Subscriptions
          </template>
          <template v-else-if="order.coins">
            Coins
          </template>
          <div class="quantity-widget">
            <IconMinus
              :class="{ disabled: maxQuantity <= minQuantity || paymentInfo.plan.quantity <= minQuantity }"
              class="decrease"
              @click="$store.commit('checkout/setQuantity', { quantity: -1 })"
            />
            <input
              :value="maxQuantity < paymentInfo.plan.quantity ? maxQuantity : paymentInfo.plan.quantity"
              type="text"
              class="quantity"
              :class="{ 'coins': order.coins }"
              @keydown="setQuantity"
              @keyup="setQuantityInState"
            >
            <IconPlus
              :class="{ disabled: paymentInfo.plan.quantity >= maxQuantity }"
              class="increase"
              @click="$store.commit('checkout/setQuantity', { quantity: 1 })"
            />
          </div>
        </div>
        <div v-if="paymentInfo.plan.quantity >= maxQuantity && order.business" class="quantity-warning support">
          If you want to purchase more than 30 subscriptions, please
          <span @click="$store.dispatch('modals/open', { name: `contact-${$store.state.auth.user ? 'support' : 'sales'}` })">
            contact {{ $store.state.auth.user ? 'support' : 'sales' }}</span>
        </div>
      </div>

      <div class="order-details">
        <template v-if="order.course">
          <div class="details-row title">
            <div>Course</div>
            <div>Price</div>
          </div>
          <div class="details-row highlight">
            <div>{{ order.plan.name }}</div>
            <div>{{ order.plan.price | formatCurrency(selectedCurrency) }}</div>
          </div>
          <br>
          <br>
        </template>
        <template v-else>
          <div v-show="!order.coins" class="details-row title">
            <div>Plan</div>
            <div>Price</div>
          </div>
          <div v-show="!order.coins" class="details-row highlight">
            <div>{{ order.plan.name }}</div>
            <div>
              {{ (order.plan.pricePerMonth || order.plan.pricePerYear || order.plan.price) | formatCurrency(selectedCurrency) }}
              <span v-if="order.business" class="business-price-desc">
                {{ order.monthly ? 'month' : 'year' }}/user
              </span>
              <template v-else-if="!order.lifetime">
                /month
              </template>
            </div>
          </div>
          <hr v-if="$route.query.plan !== 'lifetime' || ($route.query.plan === 'lifetime' && order.coupon.applied)">
          <div v-if="order.plan.saving.pre" class="details-row">
            <div>Base price</div>
            <div>{{ order.plan.saving.pre | formatCurrency(selectedCurrency) }}</div>
          </div>
          <div v-if="order.plan.saving.percent && !order.coupon.applied" class="details-row savings">
            <div>{{ order.plan.name }} Savings <span class="promo-discount">{{ order.plan.saving.percent }}%</span></div>
            <div>-{{ order.plan.saving.amount | formatCurrency(selectedCurrency) }}</div>
          </div>
          <div v-if="+order.plan.saving.tieredPricing" class="details-row">
            <div>Tiered Pricing Savings</div>
            <div>-{{ order.plan.saving.tieredPricing | formatCurrency(selectedCurrency) }}</div>
          </div>
          <div v-if="+order.plan.saving.proration" class="details-row">
            <div>Proration Savings</div>
            <div>-{{ order.plan.saving.proration | formatCurrency(selectedCurrency) }}</div>
          </div>
          <div v-if="order.coins && +order.coinSavings" class="details-row">
            <div>Coin Savings</div>
            <div>-{{ order.coinSavings | formatCurrency(selectedCurrency) }}</div>
          </div>
        </template>
        <div v-if="order.coins && order.coupon.applied" class="details-row promo">
          <div>Benefit Savings <span class="promo-discount">{{ order.coupon.discountPercent }}%</span></div>
          <div>-{{ order.discount | formatCurrency(selectedCurrency) }}</div>
        </div>
        <div v-else-if="order.coupon.applied" class="details-row promo">
          <div>Promo Code Savings <span class="promo-discount">{{ order.coupon.discountPercent }}%</span></div>
          <div>-{{ order.discount | formatCurrency(selectedCurrency) }}</div>
        </div>
        <hr class="pre-subtotal">
        <div class="details-row subtotal">
          <div>Subtotal</div>
          <div>{{ order.subtotal | formatCurrency(selectedCurrency) }}</div>
        </div>
        <div class="details-row tooltip-holder">
          <div>
            Tax <IconQuestion class="icon-question" />
            <div class="tooltip">
              Depending on your country of residence, additional tax may be applied.
            </div>
          </div>
          <div>{{ order.tax | formatCurrency(selectedCurrency) }}</div>
        </div>
        <div v-if="order.proratedCredit" class="details-row tooltip-holder">
          <div>
            Prorated Credit <IconQuestion class="icon-question" />
            <div class="tooltip">
              Prorated credit for (Subscription Plans) {{ order.plan.current }}
            </div>
          </div>
          <div>-{{ order.proratedCredit | formatCurrency(selectedCurrency) }}</div>
        </div>
        <hr>
        <div class="details-row total">
          <div>
            <template v-if="order.business && order.trial">
              Pay Now
            </template>
            <template v-else>
              Total
            </template>
          </div>
          <div>{{ order.total | formatCurrency(selectedCurrency) }}</div>
        </div>
      </div>
      <div v-if="showChangedInUSDInfo" class="currency-alert">
        *Shown in {{ selectedCurrency.value }}, but charged in USD
      </div>
      <div v-if="$config.platform === 'fa' || (order.coins && selectedCurrency.value === 'USD')" class="currency-alert">
        *Prices are in USD
      </div>
      <div class="charge" :class="{ important: (order.business && order.trial) || order.freeAccess }">
        <template v-if="(order.business && order.trial) || order.freeAccess">
          <IconWarning class="icon" />
          <template v-if="order.business && order.trial">
            You will be charged {{ order.totalAfterTrial | formatCurrency(selectedCurrency) }} on {{ order.nextBilling | formatDate }}.
          </template>
          <template v-else-if="order.freeAccess">
            You will be charged {{ order.totalAfterFreeAccess | formatCurrency(selectedCurrency) }} on {{ order.nextBilling | formatDate }}.
          </template>
        </template>
        <template v-else>
          You will be charged {{ order.total | formatCurrency(selectedCurrency) }} today.
          <template v-if="order.course || order.lifetime">
            This one-off payment provides you with lifetime access
          </template>
          <template v-else-if="order.coins">
            Coin purchases are non-refundable
          </template>
          <template v-else>
            Your next billing date will be on {{ order.nextBilling | formatDate }}.
          </template>
        </template>
      </div>
      <div v-if="currentTab === 'payment'" class="payment">
        <button
          v-if="paymentInfo.method === 'card' || (paymentInfo.method === 'pay_pal' && !paymentInfo.edit && hasPaymentProfile)"
          class="btn-primary btn-lg order-button"
          :disabled="loading || loadingChargify || !$store.state.auth.user || currentTab !== 'payment' || !hasSufficientQuantity"
          :data-event="`${$route.query.plan}_completepayment.click`"
          data-async-event
          data-direct-send
          @click="$store.dispatch('checkout/getChargifyTokenAndPay')"
        >
          Complete Payment
        </button>
        <div
          v-show="showPayPalButton"
          id="pay-pal"
          :class="{ show: showPayPalButton }"
          :disabled="loading || loadingChargify || !$store.state.auth.user || currentTab !== 'payment' || selectedCurrency.value === 'INR' || !hasSufficientQuantity"
        />
        <div v-if="$store.state.auth.user" class="support">
          Need help? <span @click="$store.dispatch('modals/open', { name: 'feedback' })">Contact support</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import IconMinus from '@itbpbg/lms-components/src/assets/svg/checkout-minus-icon.svg?vue-component';
import IconPlus from '@itbpbg/lms-components/src/assets/svg/checkout-plus-icon.svg?vue-component';
import IconArrow from '../../assets/svg/icon-arrow.svg?vue-component';
import IconQuestion from '../../assets/svg/icon-question.svg?vue-component';
import IconClose from '../../assets/svg/icon-close.svg?vue-component';
import IconWarning from '../../assets/svg/warning-gray.svg?vue-component';
import Loader from '../Loader';

export default {
  components: {
    IconMinus, IconPlus, IconArrow, IconQuestion, IconClose, Loader, IconWarning,
  },
  filters: {
    formatDate(v) {
      return new Date(v).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    },
  },
  data() {
    return {
      course: this.$route.query.course,
      plan: this.$route.query.plan,
      rkey: this.$route.query.rkey,
      expanded: false,
      dropdownOpen: false,
      hasPromoCode: false,
    };
  },
  computed: {
    ...mapState('checkout', ['chargify', 'paymentInfo', 'order', 'currentTab', 'loadingChargify', 'loading']),
    ...mapGetters({
      selectedCurrency: 'checkout/currency',
    }),
    showCurrencyDropdown() {
      if (this.$config.platform === 'fa') {
        return false;
      }

      return this.selectedCurrency && !this.order.business && !this.order.coins && !this.order.upgrade && !this.order.extend && !(this.hasPaymentProfile && this.paymentInfo.method === 'pay_pal' && !this.paymentInfo.edit);
    },
    coupon: {
      get() {
        return this.paymentInfo.plan.coupon.value;
      },
      set(value) {
        this.$store.commit('checkout/setCoupon', value);
      },
    },
    showPayPalButton() {
      return (this.paymentInfo.method === 'pay_pal' && !(this.hasPaymentProfile)) || (this.paymentInfo.method === 'pay_pal' && this.paymentInfo.edit);
    },
    hasPaymentProfile() {
      return this.order.paymentInfo && this.order.paymentInfo.paymentProfileId;
    },
    hasSufficientQuantity() {
      if (!this.order.business) {
        return true;
      }

      return this.paymentInfo.plan.quantity >= this.minQuantity && this.paymentInfo.plan.quantity <= this.maxQuantity;
    },
    maxQuantity() {
      if (this.order.coins) {
        return 10000;
      }

      if (!this.order.changeAllocation) {
        return 30;
      }

      return 30 - this.order.currentQuantity;
    },
    minQuantity() {
      if (!this.order.changeAllocation && this.order.business) {
        return 2;
      }

      return 1;
    },
    showChangedInUSDInfo() {
      if (this.paymentInfo.method === 'pay_pal' && this.order.paymentInfo.paymentType === 'pay_pal' && this.order.paymentInfo.paymentProfileId && this.selectedCurrency.value !== 'USD') {
        return false;
      }

      if (this.order.coins || this.order.upgrade || this.order.extend) {
        return false;
      }

      return this.$config.platform === 'ds' && this.selectedCurrency.value !== 'USD';
    },
  },
  watch: {
    currentTab(v) {
      if (v === 'payment') {
        this.expanded = true;
        this.$store.dispatch('checkout/setPaymentCart', {
          type: 'new-subscription',
        });
      }
    },
  },
  mounted() {
    this.$root.$on('emit-click', (e) => {
      if (
        !e.target.closest('.currency')
        || e.target.classList.contains('currency')
      ) {
        this.dropdownOpen = false;
      }
    });

    if (this.coupon && this.$route.query.coupon) {
      this.hasPromoCode = true;
    }
  },
  methods: {
    async removeCode() {
      this.coupon = '';
      this.$store.commit('checkout/setCouponRemoved', true);
      await this.$store.dispatch('checkout/fetchOrderSummary', { action: 'removeCoupon' });
      this.hasPromoCode = false;
    },
    expandSection(e) {
      if (this.$store.state.layout === 'desktop' || this.currentTab === 'payment') {
        return;
      }

      if (e.target.classList.contains('currency') || e.target.closest('.currency')) {
        return;
      }

      this.expanded = !this.expanded;
      this.$emit('expand', this.expanded);
    },
    setQuantity(e) {
      const quantity = +e.key;

      if (!quantity && e.key !== 'Backspace' && e.key !== 'Delete' && quantity !== 0 && e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') {
        e.preventDefault();
      }
    },
    setQuantityInState(e) {
      setTimeout(() => {
        let quantity = +e.target.value;
        if (quantity > this.maxQuantity) {
          quantity = this.maxQuantity;
        } else if (quantity < this.minQuantity) {
          quantity = this.minQuantity;
        }

        // eslint-disable-next-line no-param-reassign
        e.target.value = quantity;
        this.$store.commit('checkout/setQuantity', { quantity, input: true });
      }, 1600);
    },
  },
};
</script>

<style lang="scss">
.order-summary {
  background-color: var(--main-dark-shade-2);
  border-radius: 10px;
  padding: var(--spacing-36) var(--spacing-24);

  .heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-32);
    position: relative;

    .h5 {
      margin: 0;
      color: var(--main-light);

      .expand-arrow {
        display: none;
      }
    }

    .total-price {
      display: none;
    }

    .currency {
      position: relative;
      cursor: pointer;

      &.loading {
        pointer-events: none;
        opacity: 0.3;
      }

      svg path[stroke] {
        stroke: var(--main-light-shade-4);
      }

      ul {
        position: absolute;
        top: 24px;
        right: 0;
        min-width: 80px;
        margin: 0;
        padding: 0;
        background-color: var(--main-dark-shade-2);
        border: 1px solid #3D4450;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
        border-radius: 3px;

        li {
          list-style: none;
          font-size: 14px;
          color: var(--main-light);
          padding: 16px;
        }
      }
    }
  }

  .order-summary-loader {
    z-index: 8;
    right: 0;
    left: auto;
    top: 50%;
    bottom: auto;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    background: transparent;

    svg {
      width: 28px;
      transform: translate(-50%, -50%);
      margin: 0;

      circle[fill] {
        fill: var(--main-light-shade-4) !important;
      }
    }

    &.coins {
      position: absolute;
    }
  }

  .content {
    &.loading {
      position: relative;
    }
  }

  .promo-code {
    margin-bottom: var(--spacing-28);

    &.loading {
      pointer-events: none;
      opacity: 0.3;
      position: relative;
    }

    .promo-text {
      font-size: 14px;
      font-weight: 500;
      text-decoration: underline;
      color: rgba(171, 175, 181, 0.8);
      cursor: pointer;
    }

    .promo-input-wrapper {
      .promo-input {
        display: flex;
        align-items: flex-start;
        margin-bottom: 12px;

        > div {
          margin-right: var(--spacing-8);

          input {
            height: 58px;
            background-color: #4B5461;
            border: 1px solid #565E6A;
            border-radius: 3px;
            color: var(--main-light);

            &::placeholder {
              color: var(--main-light-shade-4);
            }

            &:-ms-input-placeholder {
              color: var(--main-dark-shade-4);
            }

            &::-ms-input-placeholder {
              color: var(--main-dark-shade-4);
            }

            &.invalid {
              border-color: var(--main-red-shade-1);
            }
          }

          span {
            color: var(--main-red-shade-1);
            font-size: 14px;
            font-weight: 500;
            margin-top: 4px;
            display: inline-block;
          }
        }
      }
    }

    .promo-applied {
      font-size: 14px;
      font-weight: 500;
      color: var(--main-light);
      display: flex;
      flex-wrap: wrap;
      align-items: center;

      p {
        margin: 0 var(--spacing-8) var(--spacing-4) 0;
        // margin-bottom: 20px;
        // padding-bottom: var(--spacing-8);
      }

      .promo-discount {
        white-space: nowrap;
        margin-left: 0;
        margin-bottom: var(--spacing-4);
        padding: var(--spacing-8) 11px var(--spacing-8) var(--spacing-8);
        text-transform: uppercase;

        svg {
          width: 10px;
          margin-left: 10px;

          path[stroke] {
            stroke: var(--accent-shade-3);
          }
        }
      }
    }
  }

  .promo-discount {
    padding: 2px 5px;
    font-weight: 700;
    color: var(--accent);
    background: rgba(86, 191, 199, 0.2);
    border-radius: 3px;
    margin-left: var(--spacing-8);
  }

  .subscriptions-quantity-widget {
    margin-bottom: 36px;

    .subscriptions-quantity {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .quantity-widget {
        display: flex;
        align-items: center;

        svg {
          &.disabled {
            opacity: 0.3;
            pointer-events: none;
          }
        }

        .increase,
        .decrease {
          user-select: none;
          width: 20px;
          height: 20px;
          cursor: pointer;
        }

        .quantity {
          width: 54px;
          height: 34px;
          font-size: 18px;
          font-weight: 500;
          background-color: #4B5461;
          border: 1px solid #565E6A;
          border-radius: 3px;
          color: var(--main-light);
          margin: 0 8px;
          padding: 14px 15px;
          text-align: center;

          &.coins {
            width: 64px;
          }
        }
      }
    }
  }

  .quantity-warning {
    background-color: var(--main-dark-light-4);
    border: 1px solid var(--main-dark-light-3);
    color: var(--main-light);
    font-size: 14px;
    border-radius: 5px;
    margin-top: 16px !important;
    padding: 10px;
    text-align: left !important;

    span {
      text-decoration: underline;
    }
  }

  .order-details {
    .details-row {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &.title {
        margin-bottom: var(--spacing-4);
      }

      &:not(.title) {
        div:nth-of-type(2) {
          color: var(--main-light);
        }
      }

      &:not(.highlight):not(.total) {
        font-size: 14px;
      }

      &.highlight,
      &.total {
        color: var(--main-light);
        font-weight: 500;
      }

      &.total {
        font-size: 18px;
      }

      &.subtotal {
        div {
          color: var(--main-light);
        }
      }

      &.highlight {
        div:first-of-type {
          padding-right: 50px;
        }

        div:last-of-type {
          text-align: right;
        }

        & ~ .details-row {
          padding-top: var(--spacing-4);
          padding-bottom: var(--spacing-4);
        }

        .business-price-desc {
          font-size: 14px;
        }
      }

      &.promo {
        color: var(--accent);
      }

      &.tooltip-holder {
        div:first-of-type {
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;

          .icon-question {
            width: 12px;
            margin-left: var(--spacing-6);
          }

          .icon-question:hover + .tooltip {
            display: block;
          }

          .tooltip {
            display: none;
            position: absolute;
            right: 10px;
            bottom: 22px;
            width: 220px;
            padding: var(--spacing-16);
            background: var(--main-light);
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
            border-radius: 3px;
            color: var(--main-dark-shade-0);
            text-align: center;
            transform: translateX(50%);
            z-index: 3;

            &:after {
              content: "";
              position: absolute;
              bottom: -5px;
              left: 50%;
              margin-left: -3px;
              height: 15px;
              width: 15px;
              background-color: var(--main-light);
              clip-path: polygon(0% 0%, 100% 100%, 0% 100%);
              transform: rotate(-45deg);
              border-radius: 0 0 0 0.25em;
            }
          }
        }
      }
    }

    hr {
      margin-top: var(--spacing-16);
      margin-bottom: var(--spacing-16);
      border: none;
      height: 1px;
      background-color: #404A5B;

      &.pre-subtotal {
        margin-top: var(--spacing-8);
        margin-bottom: var(--spacing-8);
      }
    }
  }

  .currency-alert {
    font-size: 12px;
    margin: var(--spacing-8) 0;
  }

  .charge {
    font-size: 14px;
    margin-top: var(--spacing-24);
    // margin-bottom: var(--spacing-36);
    line-height: 20px;
    color: var(--main-light);

    &.important {
      display: flex;
      align-items: center;
      border-radius: 5px;
      gap: 8px;
      background-color: var(--main-dark-light-3);
      color: var(--main-light);
      padding: var(--spacing-8) 10px;

      .icon {
        flex-shrink: 0;
      }
    }
  }

  .order-button {
    margin-top: var(--spacing-32);
    width: 100%;
  }

  #pay-pal {
    &[disabled] {
      opacity: 0.3;
      pointer-events: none;
    }

    &.show {
      display: block !important;
    }
  }

  .footer-note {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    font-weight: 500;

    svg {
      margin-right: var(--spacing-8);
    }
  }

  .support {
    margin-top: 32px;
    text-align: center;
    font-size: 14px;

    span {
      cursor: pointer;
      color: var(--accent);
      font-weight: 600;
    }
  }
}

@include bp($bp-mobile) {
  .order-summary {
    margin-left: -24px;
    margin-right: -24px;
    padding-top: 0;
    padding-bottom: 28px;
    border-radius: 0;
    max-height: 75px;
    overflow: hidden;
    transition: all var(--duration) ease-in;
    display: block;
    z-index: 1;

    .heading {
      margin-top: 28px;

      .h5 {
        display: flex;
        justify-content: center;
        align-items: center;

        .expand-arrow {
          display: block;
          margin-left: var(--spacing-8);
          transform: rotate(180deg);

          path[stroke] {
            stroke: var(--main-light);
          }
        }
      }

      .total-price {
        display: block;
        font-size: 18px;
        color: var(--main-light);
      }

      .currency {
        display: none;
      }
    }

    &.expanded {
      position: static;
      width: auto;
      border-radius: 10px 10px 0 0;
      max-height: 1000px;

      .heading {
        .h5 {
          .expand-arrow {
            transform: rotate(0deg);
          }
        }

        .total-price {
          display: none;
        }

        .currency {
          display: block;
        }
      }

      .content {
        max-height: 2000px;
        opacity: 1;
        overflow: unset;
      }
    }

    .order-details {
      .details-row.tooltip-holder div:first-of-type .tooltip {
        right: auto;

        &:after {
          left: 12px;
        }
      }
    }

    .content {
      overflow: hidden;
      max-height: 0;
      opacity: 0;
      transition: all 500ms ease-in;
    }

    .secure-icons {
      position: static;
    }
  }
}

.fa {
  .order-summary {
    background-color: var(--main-dark-light-1);

    .footer-note {
      margin-top: var(--spacing-20);
    }

    .support {
      margin-top: var(--spacing-36);
    }

    .promo-code {
      margin-bottom: var(--spacing-32);

      .promo-discount {
        svg {
          path[stroke] {
            stroke: var(--accent-shade-1);
          }
        }
      }

      .promo-input-wrapper {
        .promo-input {
          > div input {
            background-color: var(--background-700);

            &:not(.invalid) {
              border-color: var(--main-dark-shade-3);
            }

            &:focus {
              border-color: var(--main-light);
            }
          }
        }
      }
    }

    .order-details {
      hr {
        background-color: var(--main-dark-light-3);
      }

      .details-row {
        &.promo {
          color: var(--accent-light-3);
        }

        &.subtotal {
          > div {
            &:first-of-type {
              color: var(--main-light-shade-4);
            }
          }
        }
      }
    }

    .charge {
      margin-top: var(--spacing-32);
      margin-bottom: 0;
    }

    .promo-discount {
      background: rgba(53, 140, 253, 0.2);
    }
  }
}
</style>
